import {useEffect} from 'react';
import {useDispatch} from 'react-redux';

import type {
    SubmissionFragment_primaryEntity as PrimaryEntity,
    SubmissionFragment_formVersion_form as Form,
} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

import {OpenableModals} from '../../openable-modals/openable-modals';
import {closeModal} from '../../openable-modals/openable-modals-actions';

import {useGetSubmission} from './graphql/hooks/use-get-submission';
import {ViewFormSubmissionModal} from './view-form-submission-modal';

interface Props {
    submissionId: string;
    primaryEntity?: PrimaryEntity;
    submissionTime?: number;
    form?: Form;
}

export function FormSubmissionModalWrapper(props: Props) {
    const dispatch = useDispatch();

    // Close modal when route changes
    useEffect(() => {
        const unlisten = appHistory.listen(() => {
            dispatch(closeModal(OpenableModals.FORM_SUBMISSION_MODAL));
        });

        return unlisten;
    }, [dispatch]);

    const {submission, submittedFields, submissionUrl, loading} = useGetSubmission(
        props.submissionId
    );

    const primaryEntity = props.primaryEntity ?? submission?.primaryEntity;
    const submissionTime = props.submissionTime ?? submission?.submissionTime;
    const form = props.form ?? submission?.formVersion?.form;

    const getHtmlUrlPath = () => {
        if (!primaryEntity) {
            return '';
        }

        if (primaryEntity.__typename === 'Lead') {
            return primaryEntity.htmlUrlPath;
        }

        try {
            return new URL(primaryEntity.htmlUrl).pathname;
        } catch {
            return '';
        }
    };

    const getEntityType = () => {
        switch (primaryEntity?.__typename) {
            case 'Account':
                return 'accounts';
            case 'Lead':
                return 'leads';
            case 'Contact':
            default:
                return 'contacts';
        }
    };

    if (!submissionTime || !form) {
        return null;
    }

    const getInitials = () => {
        if (primaryEntity?.__typename === 'Account' || primaryEntity?.__typename === 'Contact') {
            return primaryEntity?.initials;
        }

        return undefined;
    };

    return (
        <ViewFormSubmissionModal
            onClose={() => dispatch(closeModal(OpenableModals.FORM_SUBMISSION_MODAL))}
            submissionTime={submissionTime}
            submittedFields={submittedFields}
            submissionUrl={submissionUrl}
            notificationLogs={submission?.notificationLogs ?? []}
            isLoadingSubmission={loading}
            submitter={
                primaryEntity
                    ? {
                          type: getEntityType(),
                          name: primaryEntity.name,
                          htmlUrlPath: getHtmlUrlPath(),
                          avatarUrl: primaryEntity.avatarUrl,
                          initials: getInitials(),
                      }
                    : {
                          type: 'contacts',
                          name: 'Someone',
                          htmlUrlPath: '',
                          avatarUrl: '',
                      }
            }
            form={form}
        />
    );
}
